import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  TimesheetPageEmployeesSettingFragment,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import CreateRequestModal from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/EditTimesheetModal/CreateRequestModal'
import NewShiftForm from 'modules/attendance/components/ClockIn/Shifts/HourlyShifts/HourlyTable/DayRow/ShiftPanelTableBody/ShiftRow/NewShiftForm'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { AttendanceBreakConfigurations } from 'modules/attendance/hooks/useGetAttendanceBreakConfigurationsRange'
import { useTimeTrackingFeatureAvailabilityForCurrentCompany } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'

interface AddButtonProps {
  attendanceSetting: TimesheetPageEmployeesSettingFragment
  date: moment.Moment
  dayBreaks: AttendanceBreakConfigurations[]
  lastShift: TimesheetPageShiftFragment | undefined
  showStartsNextDayCheckbox: boolean
  store: Store
}

const AddShiftButton = ({
  attendanceSetting,
  date,
  dayBreaks,
  lastShift,
  showStartsNextDayCheckbox,
  store,
}: AddButtonProps) => {
  const [showEditTimesheetModal, setShowEditTimesheetModal] = React.useState(false)

  const hasBreaksEnabled = !!attendanceSetting?.enableBreaks
  const hasLocationEnabled = !!attendanceSetting?.enableClockInLocation

  const editTimesheetAuthorizationEnabled = useTimeTrackingFeatureAvailabilityForCurrentCompany(
    'time_tracking_edit_timesheet_authorization'
  )
  const hasEditTimesheetAuthorization =
    editTimesheetAuthorizationEnabled && !!attendanceSetting?.editTimesheetAuthorization

  const { running: refreshingTimesheet, performAction: refreshTimesheet } = useAsyncAction(
    async () => {
      await store.fetch(date)
    }
  )

  const handleOnClickAdd = () => {
    track(trackingEvents.timeTracking.addShiftRequestAddStart)
    setShowEditTimesheetModal(true)
  }

  return (
    <Box
      width="full"
      extra={{ 'data-intercom-target': 'attendance-row-add-shift-button-container' }}
    >
      {hasEditTimesheetAuthorization ? (
        <>
          <Box
            alignItems="flexStart"
            width="full"
            paddingLeft="s32"
            maxHeight="s32"
            extra={{
              'data-intercom-target': 'attendance-row-add-shift-button',
              'data-testid': 'add-shift-button',
            }}
          >
            <Button type="secondary" onClick={handleOnClickAdd} iconName="add">
              {i18n.t('common.add')}
            </Button>
          </Box>
          {showEditTimesheetModal && !refreshingTimesheet && (
            <CreateRequestModal
              onCloseModal={() => setShowEditTimesheetModal(false)}
              refreshTimesheet={refreshTimesheet}
              date={date}
              hasLocationEnabled={hasLocationEnabled}
              hasBreaksEnabled={hasBreaksEnabled}
              lastShift={lastShift}
              dayBreaks={dayBreaks}
              employeeId={store.employeeId}
              showStartsNextDayCheckbox={showStartsNextDayCheckbox}
            />
          )}
        </>
      ) : (
        <NewShiftForm
          attendanceSetting={attendanceSetting}
          date={date}
          dayBreaks={dayBreaks}
          employeeId={store.employeeId}
          hasBreaksEnabled={hasBreaksEnabled}
          hasLocationEnabled={hasLocationEnabled}
          lastShift={lastShift}
          showStartsNextDayCheckbox={showStartsNextDayCheckbox}
        />
      )}
    </Box>
  )
}

export default observer(AddShiftButton)
